import React from "react";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import { PanelEmptyMessage } from "@design-system/PanelEmptyMessage/PanelEmptyMessage";
import { VStack } from "@design-system/VStack/VStack";
import { ActivityTimeline } from "@design-system/ActivityTimeline/ActivityTimeline";
import { AccrualActivityRow } from "./AccrualActivityRow";
import {
    AccrualActivityResourceType,
    AccrualActivityState,
    getAccrualEntityLabel,
} from "./useAccrualActivity";
import styles from "./AccrualActivity.module.scss";

interface AccrualActivityListProps {
    activity: AccrualActivityState;
    resourceType: AccrualActivityResourceType;
    resourceId: string;
    entityId: number;
}

/**
 * @purpose Loading / error / empty / paginated rendering of one document's
 * audit log. The section header and expand state belong to the caller
 * (`AccrualActivity` standalone, `AccrualActivityPanel` inside an accordion).
 */
export const AccrualActivityList: React.FC<AccrualActivityListProps> = ({
    activity,
    resourceType,
    resourceId,
    entityId,
}) => {
    const {
        logs,
        hasMore,
        nextPageSize,
        isPending,
        isFetching,
        isSuccess,
        isError,
        errorMessage,
        refetch,
        loadMore,
    } = activity;

    return (
        <VStack gap={12} id={`accrual-activity-list-${resourceId}`}>
            {isPending && (
                <HStack justify="center" padding={16}>
                    <Loader />
                </HStack>
            )}
            {isError && (
                <VStack gap={8} align="center" padding={16}>
                    <p className={styles.errorMessage}>{errorMessage}</p>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={refetch}
                        disabled={isFetching}
                    >
                        Retry
                    </Button>
                </VStack>
            )}
            {isSuccess && logs.length === 0 && (
                <PanelEmptyMessage>No activity yet</PanelEmptyMessage>
            )}
            {logs.length > 0 && (
                <>
                    <ActivityTimeline>
                        {logs.map((log) => (
                            <AccrualActivityRow
                                key={log.id}
                                log={log}
                                entityLabel={getAccrualEntityLabel(
                                    resourceType,
                                )}
                                entityId={entityId}
                            />
                        ))}
                    </ActivityTimeline>
                    {hasMore && (
                        <HStack justify="center">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={loadMore}
                                disabled={isFetching}
                            >
                                {isFetching
                                    ? "Loading…"
                                    : `Load ${nextPageSize} more`}
                            </Button>
                        </HStack>
                    )}
                </>
            )}
        </VStack>
    );
};
